import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  FileImage,
  FileText,
  Info,
  Loader2
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode
} from "react";
import { toast } from "sonner";

import { FormSheetActions, FormSheetFrame } from "@/components/form";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import { ForeignNationalDocumentsAPI } from "../../../api/foreignNationalDocumentsApi";
import { MattersAPI } from "../../../api/matters";
import type { MatterDTO } from "../../../api/types/matter";
import { ErrorState } from "../../../components/common/ErrorState";
import { VisaTag } from "../../../components/common/VisaTag";
import { asVisaValue, getPetitionerName } from "../../../utils/matterDisplay";
import { AssignedUsersAvatarStack } from "../../foreignNationals/components/AssignedUsersAvatarStack";
import { CaseStatusPill } from "../../matters/components/CaseStatusPill";
import { useUserProfiles } from "../../userProfiles/context/UserProfilesContext";
import { docIsReady, formatBytesMb } from "../documentsModel";
import type { ForeignNationalDocument } from "../types";

type StepKey = "review" | "select_case" | "confirm";
const STEP_ORDER: Record<StepKey, number> = {
  review: 0,
  select_case: 1,
  confirm: 2
};

type Props = {
  open: boolean;
  foreignNationalId: string;
  documents: ForeignNationalDocument[];
  onOpenChange: (open: boolean) => void;
  onSuccess: (opts: { sentCount: number; caseLabel: string }) => void;
};

const matterLabel = (m: MatterDTO): string => {
  const visa = String(m.case?.visa ?? "").trim();
  const status = String(m.case?.status ?? "").trim();
  const base = visa || "Case";
  return status ? `${base} • ${status}` : base;
};

const formatMdY = (value?: string | null): string => {
  const raw = String(value ?? "").trim();
  if (!raw) return "-";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "-";
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(
    d.getDate()
  ).padStart(2, "0")}/${d.getFullYear()}`;
};

const MUTED = "text-xs text-muted-foreground";
const HEAD_CLASS =
  "sticky top-0 z-10 bg-muted text-xs font-semibold tracking-wide text-muted-foreground uppercase";

const MatterSkeletonRow: FC = () => (
  <TableRow>
    <TableCell className="w-10 px-2">
      <Skeleton className="mx-auto size-4 rounded-sm" />
    </TableCell>
    <TableCell>
      <Skeleton className="h-4 w-16" />
    </TableCell>
    <TableCell>
      <Skeleton className="h-4 w-20" />
    </TableCell>
    <TableCell>
      <Skeleton className="h-4 w-20" />
    </TableCell>
    <TableCell>
      <Skeleton className="h-4 w-28" />
    </TableCell>
    <TableCell>
      <div className="flex gap-1">
        <Skeleton className="size-5 rounded-xs" />
        <Skeleton className="size-5 rounded-xs" />
      </div>
    </TableCell>
    <TableCell>
      <Skeleton className="h-4 w-20" />
    </TableCell>
  </TableRow>
);

/** Row that selects its matter on click / Enter / Space (except from nested controls). */
const MatterRow: FC<{
  label: string;
  selected: boolean;
  onActivate: () => void;
  children: ReactNode;
}> = ({ label, selected, onActivate, children }) => {
  const handleClick = (event: MouseEvent<HTMLTableRowElement>) => {
    const target = event.target as HTMLElement;
    if (target.closest("button, a, input, [role=checkbox]")) return;
    onActivate();
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLTableRowElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onActivate();
    }
  };
  return (
    <TableRow
      tabIndex={0}
      aria-label={label}
      aria-selected={selected}
      data-state={selected ? "selected" : undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className="cursor-pointer focus:outline-hidden focus-visible:bg-fill-1"
    >
      {children}
    </TableRow>
  );
};

const StepSection: FC<{
  label: string;
  unlocked: boolean;
  done: boolean;
  expanded: boolean;
  onToggle: () => void;
  action?: ReactNode;
  children: ReactNode;
}> = ({ label, unlocked, done, expanded, onToggle, action, children }) => (
  <section className="flex flex-col">
    <div
      className={cn(
        "flex items-center justify-between gap-3 border-y border-border bg-fill-1 px-4 py-2",
        !unlocked && "opacity-60"
      )}
    >
      <button
        type="button"
        disabled={!unlocked}
        aria-expanded={expanded}
        onClick={onToggle}
        className="inline-flex flex-1 items-center gap-1.5 text-left text-xs font-semibold tracking-wide text-muted-foreground uppercase disabled:cursor-default"
      >
        <ChevronDown
          className={cn(
            "size-3.5 transition-transform",
            expanded ? "rotate-0" : "-rotate-90"
          )}
        />
        {label}
      </button>
      <div className="flex items-center gap-2">
        {!unlocked ? null : done ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-success">
            <CheckCircle2 className="size-3.5" /> Completed
          </span>
        ) : (
          action
        )}
      </div>
    </div>
    {expanded ? children : null}
  </section>
);

/** Three-step sheet: review the selection, pick a case, confirm the copy. */
export function ForeignNationalDocumentsSendToCaseDrawer({
  open,
  foreignNationalId,
  documents,
  onOpenChange,
  onSuccess
}: Props) {
  const { get: getUserProfile, ensure: ensureUserProfiles } = useUserProfiles();

  const [step, setStep] = useState<StepKey>("review");
  const [expanded, setExpanded] = useState<Set<StepKey>>(
    () => new Set(["review"])
  );
  const [matters, setMatters] = useState<MatterDTO[]>([]);
  const [isLoadingMatters, setIsLoadingMatters] = useState(false);
  const [mattersError, setMattersError] = useState<string | null>(null);
  const fetchedForRef = useRef<string | null>(null);
  const loadingRef = useRef(false);
  const [selectedMatter, setSelectedMatter] = useState<MatterDTO | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [showAllDocs, setShowAllDocs] = useState(false);

  const readyDocs = useMemo(() => documents.filter(docIsReady), [documents]);
  const hasUnready = readyDocs.length !== documents.length;

  const fnProfile = useMemo(() => {
    const id = (foreignNationalId ?? "").trim();
    return id ? getUserProfile(id) ?? null : null;
  }, [foreignNationalId, getUserProfile]);
  const fnAvatarUrl = (fnProfile?.avatar_data_url ?? "").trim() || undefined;
  const fnInitials = useMemo(() => {
    const first = (fnProfile?.first_name ?? "").trim();
    const last = (fnProfile?.last_name ?? "").trim();
    const fromName = `${first[0] ?? ""}${last[0] ?? ""}`.toUpperCase();
    if (fromName) return fromName;
    return ((fnProfile?.email ?? "").trim()[0] ?? "F").toUpperCase();
  }, [fnProfile]);

  useEffect(() => {
    if (!open) return;
    const id = (foreignNationalId ?? "").trim();
    if (id) ensureUserProfiles([id]).catch(() => undefined);
  }, [ensureUserProfiles, foreignNationalId, open]);

  // Reset on every open.
  useEffect(() => {
    if (!open) return;
    setStep("review");
    setExpanded(new Set(["review"]));
    setSelectedMatter(null);
    setMatters([]);
    setMattersError(null);
    loadingRef.current = false;
    setIsLoadingMatters(false);
    fetchedForRef.current = null;
    setIsSending(false);
    setSendError(null);
    setShowAllDocs(false);
  }, [open]);

  const goToStep = useCallback((next: StepKey) => {
    setStep(next);
    setExpanded(new Set([next]));
  }, []);

  const fetchMatters = useCallback(
    async (opts?: { force?: boolean }) => {
      if (!foreignNationalId || loadingRef.current) return;
      if (fetchedForRef.current === foreignNationalId && !opts?.force) return;
      fetchedForRef.current = foreignNationalId;
      loadingRef.current = true;
      setIsLoadingMatters(true);
      setMattersError(null);
      try {
        const items = await MattersAPI.listForForeignNational(
          foreignNationalId
        );
        setMatters(Array.isArray(items) ? items : []);
      } catch (err) {
        setMattersError(
          err instanceof Error ? err.message : "Failed to load cases"
        );
      } finally {
        loadingRef.current = false;
        setIsLoadingMatters(false);
      }
    },
    [foreignNationalId]
  );

  useEffect(() => {
    if (!open) return;
    if (step === "select_case" || expanded.has("select_case"))
      fetchMatters().catch(() => undefined);
  }, [expanded, fetchMatters, open, step]);

  const selectedCaseLabel = selectedMatter ? matterLabel(selectedMatter) : "";
  const shownDocs = showAllDocs ? readyDocs : readyDocs.slice(0, 10);
  const totalBytes = readyDocs.reduce(
    (sum, d) => sum + (Number(d.size_bytes) || 0),
    0
  );

  const canContinueFromReview = !hasUnready && documents.length > 0;
  const canContinueFromSelect = Boolean(selectedMatter);
  const canSend = Boolean(selectedMatter) && !hasUnready && !isSending;

  const send = useCallback(async () => {
    if (
      !foreignNationalId ||
      !selectedMatter ||
      documents.length === 0 ||
      hasUnready
    )
      return;
    setIsSending(true);
    setSendError(null);
    try {
      await ForeignNationalDocumentsAPI.sendToCase(foreignNationalId, {
        individual_id: selectedMatter.individual_id,
        case_id: selectedMatter.case_id,
        foreign_national_document_ids: readyDocs.map(
          (d) => d.foreign_national_document_id
        )
      });
      toast.success("Sent to case", {
        description: `${readyDocs.length} document${
          readyDocs.length === 1 ? "" : "s"
        } sent to ${selectedCaseLabel}`
      });
      onSuccess({ sentCount: readyDocs.length, caseLabel: selectedCaseLabel });
      onOpenChange(false);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Failed to send documents";
      setSendError(msg);
      toast.error("Send to case failed", { description: msg });
    } finally {
      setIsSending(false);
    }
  }, [
    documents.length,
    foreignNationalId,
    hasUnready,
    onOpenChange,
    onSuccess,
    readyDocs,
    selectedCaseLabel,
    selectedMatter
  ]);

  const toggle = (key: StepKey) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const subtitle =
    step === "review"
      ? "Review selected files"
      : step === "select_case"
      ? "Select a case"
      : "Confirm";
  const current = STEP_ORDER[step];

  const footer = (
    <FormSheetActions
      start={
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={isSending}
          onClick={() => onOpenChange(false)}
        >
          Cancel
        </Button>
      }
    >
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={isSending || step === "review"}
        onClick={() => goToStep(step === "confirm" ? "select_case" : "review")}
      >
        Back
      </Button>
      {step !== "confirm" ? (
        <Button
          variant="primary"
          type="button"
          size="sm"
          disabled={
            isSending ||
            (step === "review" && !canContinueFromReview) ||
            (step === "select_case" && !canContinueFromSelect)
          }
          onClick={() =>
            goToStep(step === "review" ? "select_case" : "confirm")
          }
        >
          Next
        </Button>
      ) : (
        <Button
          variant="primary"
          type="button"
          size="sm"
          disabled={!canSend}
          onClick={() => send().catch(() => undefined)}
        >
          {isSending ? <Loader2 className="animate-spin" /> : null}
          Confirm
        </Button>
      )}
    </FormSheetActions>
  );

  return (
    <FormSheetFrame
      open={open}
      onOpenChange={onOpenChange}
      title="Send to case"
      description={subtitle}
      size="xl"
      busy={isSending}
      footer={footer}
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto @container">
        <StepSection
          label={`Review selected files (${documents.length})`}
          unlocked
          done={current > 0}
          expanded={expanded.has("review")}
          onToggle={() => toggle("review")}
          action={
            <Button
              variant="primary"
              size="xs"
              disabled={!canContinueFromReview}
              onClick={() => goToStep("select_case")}
            >
              Confirm
            </Button>
          }
        >
          <div className="flex flex-col gap-3 px-6 py-4">
            {documents.length === 0 ? (
              <div className="rounded-md border border-border bg-fill-1 px-4 py-3 text-sm text-muted-foreground">
                No documents selected.
              </div>
            ) : null}
            {hasUnready ? (
              <Alert>
                <AlertCircle />
                <AlertTitle>Some selected files are still uploading</AlertTitle>
                <AlertDescription>
                  Remove them from selection or wait for upload to finish.
                </AlertDescription>
              </Alert>
            ) : null}
            {documents.length > 0 ? (
              <ul className="list-disc pl-5 text-xs text-muted-foreground">
                {documents.map((d) => (
                  <li key={d.foreign_national_document_id}>
                    {d.filename}
                    {!docIsReady(d) ? " (Uploading…)" : ""}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </StepSection>

        <StepSection
          label="Select a case"
          unlocked={current >= 1}
          done={current > 1}
          expanded={expanded.has("select_case")}
          onToggle={() => toggle("select_case")}
          action={
            <Button
              variant="primary"
              size="xs"
              disabled={!canContinueFromSelect}
              onClick={() => goToStep("confirm")}
            >
              Confirm
            </Button>
          }
        >
          {mattersError ? (
            <div className="px-6 py-4">
              <Alert variant="destructive">
                <AlertCircle />
                <AlertTitle>Failed to load cases</AlertTitle>
                <AlertDescription>
                  {mattersError}
                  <Button
                    variant="outline"
                    size="xs"
                    className="mt-2"
                    onClick={() =>
                      fetchMatters({ force: true }).catch(() => undefined)
                    }
                  >
                    Retry
                  </Button>
                </AlertDescription>
              </Alert>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className={cn(HEAD_CLASS, "w-10 px-2")} />
                  <TableHead className={HEAD_CLASS}>Visa Type</TableHead>
                  <TableHead className={HEAD_CLASS}>Filing Deadline</TableHead>
                  <TableHead className={HEAD_CLASS}>Status</TableHead>
                  <TableHead className={HEAD_CLASS}>Petitioner</TableHead>
                  <TableHead className={HEAD_CLASS}>Assignment</TableHead>
                  <TableHead className={HEAD_CLASS}>Date Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoadingMatters ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <MatterSkeletonRow key={i} />
                  ))
                ) : matters.length === 0 ? (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={7}>
                      <div className="flex w-full items-center justify-center py-10">
                        <ErrorState
                          title="No cases found."
                          message="Create a case for this client to send documents."
                          buttonCta=""
                          minH="auto"
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  matters.map((m) => {
                    const visa = String(m.case?.visa ?? "").trim();
                    const ind = m.individual as
                      | { is_rfe?: unknown; isRfe?: unknown }
                      | undefined;
                    const isRfe = Boolean(ind?.is_rfe ?? ind?.isRfe);
                    const petitioner = getPetitionerName(m);
                    const assigned = m.assigned_to_user_ids ?? [];
                    const createdAt = (
                      m.case as { created_at?: string | null } | undefined
                    )?.created_at;
                    const isSelected = selectedMatter?.case_id === m.case_id;
                    const label = matterLabel(m);
                    return (
                      <MatterRow
                        key={`${m.case_id}-${m.individual_id}`}
                        label={label}
                        selected={isSelected}
                        onActivate={() => setSelectedMatter(m)}
                      >
                        <TableCell className="w-10 px-2 text-center">
                          <Checkbox
                            aria-label={`Select ${label}`}
                            checked={isSelected}
                            onCheckedChange={(c) =>
                              setSelectedMatter(c === true ? m : null)
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {visa ? (
                            <VisaTag
                              visaType={asVisaValue(visa)}
                              size="sm"
                              isRfe={isRfe}
                            />
                          ) : (
                            <span className={MUTED}>-</span>
                          )}
                        </TableCell>
                        <TableCell className={MUTED}>
                          {formatMdY(m.case?.filing_deadline ?? null)}
                        </TableCell>
                        <TableCell>
                          <CaseStatusPill status={m.case?.status ?? ""} />
                        </TableCell>
                        <TableCell className="max-w-50">
                          <span
                            className={cn("block truncate", MUTED)}
                            title={petitioner === "-" ? undefined : petitioner}
                          >
                            {petitioner}
                          </span>
                        </TableCell>
                        <TableCell>
                          {assigned.length > 0 ? (
                            <AssignedUsersAvatarStack userIds={assigned} />
                          ) : (
                            <span className={MUTED}>-</span>
                          )}
                        </TableCell>
                        <TableCell className={MUTED}>
                          {formatMdY(createdAt ?? null)}
                        </TableCell>
                      </MatterRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </StepSection>

        <StepSection
          label="Confirm"
          unlocked={current >= 2}
          done={false}
          expanded={expanded.has("confirm")}
          onToggle={() => toggle("confirm")}
          action={
            <Button
              variant="primary"
              size="xs"
              disabled={!canSend}
              onClick={() => send().catch(() => undefined)}
            >
              {isSending ? <Loader2 className="animate-spin" /> : null}
              Confirm
            </Button>
          }
        >
          <div className="flex flex-col gap-4 px-6 py-5">
            <div className="flex flex-col divide-y divide-border rounded-md border border-border bg-fill-1">
              <div className="flex flex-col gap-3 px-5 py-4">
                <span className="text-sm font-semibold text-foreground">
                  Summary
                </span>
                <div className="flex items-center gap-3">
                  <Avatar className="size-10 rounded-md">
                    {fnAvatarUrl ? (
                      <AvatarImage src={fnAvatarUrl} alt="" />
                    ) : null}
                    <AvatarFallback className="rounded-md bg-primary/10 text-sm font-semibold text-primary">
                      {fnInitials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col gap-1">
                    <span className="text-sm font-semibold text-foreground">
                      {String(selectedMatter?.case?.visa ?? "").trim() ||
                        "Case"}
                    </span>
                    <CaseStatusPill
                      status={selectedMatter?.case?.status ?? ""}
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-3 px-5 py-4">
                <span className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                  Documents to send
                </span>
                <ul className="grid max-h-56 grid-cols-1 gap-x-6 gap-y-2 overflow-y-auto @md:grid-cols-2">
                  {shownDocs.map((d) => {
                    const Icon = String(d.content_type ?? "")
                      .toLowerCase()
                      .startsWith("image/")
                      ? FileImage
                      : FileText;
                    return (
                      <li
                        key={d.foreign_national_document_id}
                        className="flex items-center gap-2"
                      >
                        <Icon className="size-4 shrink-0 text-muted-foreground" />
                        <span className="min-w-0 flex-1 truncate text-xs text-foreground">
                          {d.filename}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatBytesMb(Number(d.size_bytes) || 0)}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                {readyDocs.length > 10 ? (
                  <Button
                    variant="ghost"
                    size="xs"
                    className="self-start"
                    onClick={() => setShowAllDocs((v) => !v)}
                  >
                    {showAllDocs
                      ? "Show fewer"
                      : `View all ${readyDocs.length} documents`}
                  </Button>
                ) : null}
              </div>

              <div className="flex items-center justify-between px-5 py-3 text-sm font-semibold text-foreground">
                <span>Total</span>
                <span>
                  {readyDocs.length} document
                  {readyDocs.length === 1 ? "" : "s"} •{" "}
                  {formatBytesMb(totalBytes)}
                </span>
              </div>
            </div>

            <Alert>
              <Info />
              <AlertTitle>Documents will be copied</AlertTitle>
              <AlertDescription>
                Documents will be copied to the case. Originals will remain in
                the foreign national profile.
              </AlertDescription>
            </Alert>

            {sendError ? (
              <Alert variant="destructive">
                <AlertCircle />
                <AlertTitle>Send failed</AlertTitle>
                <AlertDescription>{sendError}</AlertDescription>
              </Alert>
            ) : null}
          </div>
        </StepSection>
      </div>
    </FormSheetFrame>
  );
}
